import storage from '@/utils/storage'
import logger from '@/utils/logger'
import { HTTP_STATUS, REQUEST_METHODS } from '@/constants/api'
import {
  AUTH_MAINTENANCE_AUTH_UNAVAILABLE,
  AUTH_MAINTENANCE_ENABLED,
} from '@/config/maintenance'
import ENV from '@/config/runtime-env'
import { loginJourneyHeaders, endLoginJourney } from '@/utils/loginJourney'
import type { User, AuthToken } from '@/types'

const AUTH_TOKEN_KEY = 'auth_token'
const AUTH_USER_KEY = 'auth_user'
const API_AUTH_URL = `${ENV.apiBaseUrl}/api/auth`

/**
 * Handle API errors with specific messages
 */
/**
 * Turns an API failure into an Error the UI can act on.
 *
 * An outage carries `isOutage` so a screen can say "we are having problems,
 * please come back shortly" rather than showing a generic failure that reads
 * as though the customer did something wrong (FEDEV-528).
 */
const handleApiError = (error: unknown): Error & { status?: number; body?: Record<string, unknown>; isOutage?: boolean } => {
  const errorObj = error as Record<string, unknown>
  if (errorObj?.status === HTTP_STATUS.UNAUTHORIZED) {
    const err = new Error('Invalid credentials or session expired')
    ;(err as unknown as Record<string, unknown>).status = errorObj.status
    ;(err as unknown as Record<string, unknown>).body = errorObj.body
    return err as Error & { status?: number; body?: Record<string, unknown> }
  }
  // FEDEV-528: Dynamics is unreachable. Recognised by the flag the server
  // sets rather than the status code alone, since other things return 503 and
  // this one means "come back shortly", not "something you did was wrong".
  const outageBody = errorObj?.body as Record<string, unknown> | undefined
  if (outageBody?.outage === true) {
    const err = new Error(
      (outageBody.message as string) ||
        'We are having trouble reaching our systems right now. Please try again in a few minutes.',
    )
    ;(err as unknown as Record<string, unknown>).status = errorObj.status
    ;(err as unknown as Record<string, unknown>).body = errorObj.body
    ;(err as unknown as Record<string, unknown>).isOutage = true
    return err as Error & { status?: number; body?: Record<string, unknown> }
  }

  if (errorObj?.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    const err = new Error('Too many login attempts. Please try again later.')
    ;(err as unknown as Record<string, unknown>).status = errorObj.status
    ;(err as unknown as Record<string, unknown>).body = errorObj.body
    return err as Error & { status?: number; body?: Record<string, unknown> }
  }
  if (
    errorObj?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY
  ) {
    const bodyObj = errorObj.body as Record<string, unknown> | undefined
    const err = new Error(bodyObj?.message as string || 'Validation error')
    ;(err as unknown as Record<string, unknown>).status = errorObj.status
    ;(err as unknown as Record<string, unknown>).body = errorObj.body
    return err as Error & { status?: number; body?: Record<string, unknown> }
  }
  if (error instanceof Error) {
    ;(error as unknown as Record<string, unknown>).status =
      (error as unknown as Record<string, unknown>).status || HTTP_STATUS.INTERNAL_SERVER_ERROR
    ;(error as unknown as Record<string, unknown>).body = (error as unknown as Record<string, unknown>).body || {}
    return error as Error & { status?: number; body?: Record<string, unknown> }
  }
  const bodyObj = errorObj?.body as Record<string, unknown> | undefined
  const err = new Error(bodyObj?.message as string || 'An error occurred')
  ;(err as unknown as Record<string, unknown>).status = errorObj?.status || HTTP_STATUS.INTERNAL_SERVER_ERROR
  ;(err as unknown as Record<string, unknown>).body = bodyObj || {}
  return err as Error & { status?: number; body?: Record<string, unknown> }
}

interface InviteVerifyResult {
  email: string
  firstName?: string
  valid: boolean
}

interface LoginParams {
  password: string
  email?: string
  mobile?: string
  customer_number?: string
  /**
   * Remember me on this device. When true (default), the auth token
   * is persisted in localStorage and survives browser close. When
   * false, the token goes to sessionStorage and is gone when the tab
   * closes. Frontend-only switch; the backend doesn't know.
   */
  rememberMe?: boolean
}

export interface CheckIdentityParams {
  email?: string
  mobile?: string
  customer_number?: string
  /** Which channel to send to, when the customer has a choice (FEDEV-528). */
  channel?: 'email' | 'sms'
}

/**
 * Outcome of a /auth/check-identity call. PII-free by design — the
 * server returns just a mode plus, on `not_found`, the identifier type
 * the customer tried (so we can phrase our message accurately). No
 * display name, no masked email, nothing the customer didn't type
 * themselves comes back.
 *
 * Modes:
 *   login        — registered portal user. Step 2 = password + magic link.
 *   register     — known customer (Dynamics has them) but no portal
 *                  password yet. Step 2 = "set up your account" with
 *                  two password fields.
 *   ambiguous_identifier — the email or mobile is on more than one customer
 *                  cannot identify anybody. Step 2 = contact us.
 *   no_contact_method — nothing on file we can send a code to, e.g. a
 *                  landline only. Step 2 = contact us.
 *   not_found    — we don't know this identifier. Step 2 = "try the
 *                  other identifier; if you've tried both, please
 *                  wait for an email".
 */
/** A destination we can send a code or sign-in link to, masked server-side. */
export interface Channel {
  type: 'email' | 'sms'
  masked: string
}

/** What came back after a code was sent. */
export interface CodeSent {
  channel: 'email' | 'sms'
  masked: string
  sendsRemaining: number
}

/** The three things the single sign-in field accepts (FEDEV-528). */
export type IdentifierType = 'email' | 'mobile' | 'customer_number'

export type CheckIdentityResult =
  | { mode: 'login'; customer_number?: string; channels?: Channel[] }
  // claim_verification: when true (VERIFY_CLAIMED_ACCOUNT on the backend),
  // claiming requires a 6-digit code emailed to the address on the
  // customer's record (BUG-14: stops a mistyped or guessed customer
  // number opening a stranger's account).
  // channels: where a code may be sent, masked by the server. Identifying by
  // email or mobile proves that channel, so only that one comes back; a
  // customer number proves neither, so both are offered (FEDEV-528).
  | { mode: 'register'; customer_number?: string; channels?: Channel[] }
  // Found in CRM (or as an unclaimed local row) but no email address
  // is on file. The customer can't claim an account without one — the
  // step collects an email and posts a support request so ops can add
  // it to the Dynamics record.
  | { mode: 'add_email' }
  // The three dead ends. They differ in why we stopped, which is worth
  // logging, and are shown to the customer identically: see DeadEndReason in
  // useLoginFlow. All three carry the identifier type that was tried, so the
  // one screen can name it and suggest the other two.
  // They typed a landline. Not a dead end: a correction, so it says so
  // plainly rather than sharing the deliberately vague screen below.
  | { mode: 'landline' }
  | { mode: 'ambiguous_identifier'; tried_identifier_type?: IdentifierType }
  | { mode: 'no_contact_method'; tried_identifier_type?: IdentifierType }
  | { mode: 'not_found'; tried_identifier_type?: IdentifierType }

export interface ClaimAccountParams {
  password: string
  password_confirmation: string
  email?: string
  mobile?: string
  customer_number?: string
  verification_code?: string
}

export interface MagicLinkParams {
  email?: string
  mobile?: string
  customer_number?: string
  /** Send the link by email or by text (FEDEV-528). */
  channel?: 'email' | 'sms'
}

/**
 * FEDEV-528: the link is keyed on a single random token now, not a signed URL
 * carrying the customer's email. That works for a mobile-only customer and
 * keeps anything identifying out of the URL, where it would otherwise end up
 * in browser history and referrer headers.
 */
export interface MagicLinkConsumeParams {
  token: string
}

export type LoginSupportIssueType =
  | 'cannot_login'
  | 'email_not_recognised'
  | 'forgotten_password'
  // Lead capture from the redesigned login page's "we couldn't find
  // your account yet" path. Backend treats it like any other support
  // request; admin queue picks it up so operations can email the
  // customer once their account is provisioned.
  | 'account_not_yet_setup'
  // Customer is in CRM by customer number but the contact record
  // has no email; submitted from the add-email step in the login
  // flow so ops can update the Dynamics record.
  | 'add_email'
  // FEDEV-528: what went wrong with a sign-in code. Added alongside the
  // existing reasons, which the other entry points still use.
  | 'code_not_received'
  | 'code_too_late'
  | 'code_did_not_work'
  | 'wrong_contact_details'
  | 'other'

/** How the customer was trying to verify, when they tell us (FEDEV-528). */
export type VerificationMethod = 'email' | 'sms' | 'both' | 'unsure'

export interface LoginSupportPayload {
  issue_type: LoginSupportIssueType
  verification_method?: VerificationMethod
  name?: string
  // Either an email or a mobile is needed, so we have some way to reply.
  // Email is no longer compulsory: a customer with only a mobile is the one
  // most likely to be stuck (FEDEV-528).
  email?: string
  mobile?: string
  customer_number?: string
  postcode?: string
  house_number_or_name?: string
  details?: string
}

export interface RequestAddEmailCodePayload {
  customer_number: string
  email: string
}

export interface VerifyAddEmailPayload {
  customer_number: string
  email: string
  code: string
  password: string
  password_confirmation: string
}

interface AuthServiceInterface {
  login: (params: LoginParams) => Promise<AuthToken>
  checkIdentity: (params: CheckIdentityParams) => Promise<CheckIdentityResult>
  requestClaimCode: (params: CheckIdentityParams) => Promise<CodeSent>
  verifyClaimCode: (params: CheckIdentityParams & { code: string }) => Promise<void>
  claimAccount: (params: ClaimAccountParams) => Promise<AuthToken>
  requestMagicLink: (params: MagicLinkParams) => Promise<void>
  consumeMagicLink: (params: MagicLinkConsumeParams) => Promise<AuthToken>
  register: (userData: Record<string, unknown>) => Promise<AuthToken>
  forgotPassword: (params: { email?: string; mobile?: string; customer_number?: string }) => Promise<Response>
  submitLoginSupport: (payload: LoginSupportPayload) => Promise<Record<string, unknown>>
  requestAddEmailCode: (payload: RequestAddEmailCodePayload) => Promise<Record<string, unknown>>
  verifyAddEmail: (payload: VerifyAddEmailPayload) => Promise<AuthToken>
  resetPassword: (token: string, email: string, newPassword: string) => Promise<Response>
  refreshToken: () => Promise<Record<string, unknown>>
  logout: () => Promise<void>
  getToken: () => string | null
  getUser: () => User | null
  isAuthenticated: () => boolean
  updateProfile: (userData: Partial<User>) => Promise<Record<string, unknown>>
  verifyEmail: (email: string, code: string) => Promise<Record<string, unknown>>
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<Record<string, unknown>>
  resendVerificationEmail: (email: string) => Promise<Record<string, unknown>>
  verifyInvite: (email: string, expires: string, signature: string) => Promise<InviteVerifyResult>
  acceptInvite: (email: string, expires: string, signature: string, password: string) => Promise<AuthToken>
  clearLocalAuthSession: () => void
}

const assertAuthAvailable = (): void => {
  if (AUTH_MAINTENANCE_ENABLED) {
    throw new Error(AUTH_MAINTENANCE_AUTH_UNAVAILABLE)
  }
}

const authService: AuthServiceInterface = {
  /**
   * Login user with email or customer number and password
   */
  async login(params: LoginParams): Promise<AuthToken> {
    assertAuthAvailable()
    try {
      logger.info('Login attempt')
      const response = await fetch(`${API_AUTH_URL}/login`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          ...loginJourneyHeaders(),
        },
        body: JSON.stringify({
          // No mobile here by design (FEDEV-528): identifying by mobile has
          // already resolved to a customer number, so sign-in uses that and
          // we avoid a second CRM lookup on every password attempt.
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
          password: params.password,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const token = data?.data?.token
      const user = data?.data?.user

      if (token) {
        // Remember me chooses the storage tier:
        //   true  → localStorage (default; survives browser close)
        //   false → sessionStorage (gone the moment the tab closes)
        // getToken() falls back from one to the other so apiClient
        // doesn't need to know which path was used. Always clear the
        // OTHER store on the way in so a previous session of the
        // opposite kind doesn't shadow the new one.
        const rememberMe = params.rememberMe !== false
        if (rememberMe) {
          storage.setLocal(AUTH_TOKEN_KEY, token)
          storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
          storage.removeSession(AUTH_TOKEN_KEY)
          storage.removeSession(AUTH_USER_KEY)
        } else {
          storage.setSession(AUTH_TOKEN_KEY, token)
          storage.setSession(AUTH_USER_KEY, JSON.stringify(user))
          storage.removeLocal(AUTH_TOKEN_KEY)
          storage.removeLocal(AUTH_USER_KEY)
        }
      }

      logger.info('User logged in successfully', { rememberMe: params.rememberMe !== false })
      endLoginJourney()
      return { token, user }
    } catch (error) {
      logger.error('Login failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Two-step login — step 1: figure out what to ask for next.
   */
  async checkIdentity(params: CheckIdentityParams): Promise<CheckIdentityResult> {
    try {
      const response = await fetch(`${API_AUTH_URL}/check-identity`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      return data?.data as CheckIdentityResult
    } catch (error) {
      logger.error('Identity check failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * BUG-14: request a claim verification code. The backend emails a
   * 6-digit code to the address ALREADY on the customer's record and
   * returns a masked version of that address for display.
   */
  async requestClaimCode(params: CheckIdentityParams): Promise<CodeSent> {
    try {
      const response = await fetch(`${API_AUTH_URL}/claim-account/request-code`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
          channel: params.channel || undefined,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()

      return {
        channel: (data?.data?.channel as 'email' | 'sms') || 'email',
        // Already masked by the server; the full address or number never
        // reaches us (FEDEV-528).
        masked: (data?.data?.masked as string) || '',
        sendsRemaining: (data?.data?.sends_remaining as number) ?? 0,
      }
    } catch (error) {
      logger.error('Claim code request failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Two-step login — confirm the code on its own, before any password is
   * chosen (FEDEV-528). Keeping them apart means a wrong code and a weak
   * password cannot fail together and leave the customer guessing which.
   */
  async verifyClaimCode(params: CheckIdentityParams & { code: string }): Promise<void> {
    try {
      const response = await fetch(`${API_AUTH_URL}/claim-account/verify-code`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
          code: params.code,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }
    } catch (error) {
      logger.error('Claim code verification failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Two-step login — step 2 (set-password branch): claim the account.
   */
  async claimAccount(params: ClaimAccountParams): Promise<AuthToken> {
    try {
      const response = await fetch(`${API_AUTH_URL}/claim-account`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
          password: params.password,
          password_confirmation: params.password_confirmation,
          verification_code: params.verification_code || undefined,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const token = data?.data?.token
      const user = data?.data?.user

      if (token) {
        storage.setLocal(AUTH_TOKEN_KEY, token)
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      }

      logger.info('Account claimed and user logged in')
      endLoginJourney()
      return { token, user }
    } catch (error) {
      logger.error('Claim account failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Request a magic-login email.
   */
  async requestMagicLink(params: MagicLinkParams): Promise<void> {
    try {
      const response = await fetch(`${API_AUTH_URL}/magic-link/request`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
          // Without this the server falls back to its own preference, which
          // is email wherever it has one, so "Text me a link" quietly emailed.
          channel: params.channel || undefined,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }
    } catch (error) {
      logger.error('Magic link request failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Consume a magic-login signed link.
   */
  async consumeMagicLink(params: MagicLinkConsumeParams): Promise<AuthToken> {
    try {
      const response = await fetch(`${API_AUTH_URL}/magic-link/consume`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json', ...loginJourneyHeaders() },
        body: JSON.stringify({
          token: params.token,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const token = data?.data?.token
      const user = data?.data?.user

      if (token) {
        storage.setLocal(AUTH_TOKEN_KEY, token)
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      }

      logger.info('Magic-link login successful')
      endLoginJourney()
      return { token, user }
    } catch (error) {
      logger.error('Magic link consume failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Register new user
   */
  async register(userData: Record<string, unknown>): Promise<AuthToken> {
    assertAuthAvailable()
    try {
      logger.info('Registration a)ttempt')
      const response = await fetch(`${API_AUTH_URL}/register`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const token = data?.data?.token
      const user = data?.data?.user

      if (token) {
        storage.setLocal(AUTH_TOKEN_KEY, token)
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      }

      logger.info('User registered successfully')
      return { token, user }
    } catch (error) {
      logger.error('Registration failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Request password reset
   */
  async forgotPassword(params: {
    email?: string
    mobile?: string
    customer_number?: string
  }): Promise<Response> {
    try {
      const response = await fetch(`${API_AUTH_URL}/forgot-password`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          ...loginJourneyHeaders(),
        },
        body: JSON.stringify({
          email: params.email || undefined,
          mobile: params.mobile || undefined,
          customer_number: params.customer_number || undefined,
        }),
      })
      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }
      logger.info('Password reset requested')
      return response
    } catch (error) {
      logger.error('Forgot password failed:', error)
      throw error
    }
  },

  /**
   * Submit a login support request from the login page modal
   */
  async submitLoginSupport(payload: LoginSupportPayload): Promise<Record<string, unknown>> {
    try {
      const response = await fetch(`${API_AUTH_URL}/login-support`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Login support request submitted')
      return await response.json()
    } catch (error) {
      logger.error('Login support request failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Stage 1 of the add-email flow. Customer is in Dynamics by
   * customer number with no email on file; this asks the backend to
   * issue a 6-digit code to the email address they typed.
   */
  async requestAddEmailCode(payload: RequestAddEmailCodePayload): Promise<Record<string, unknown>> {
    try {
      const response = await fetch(`${API_AUTH_URL}/add-email/request-code`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }
      logger.info('Add-email code requested')
      return await response.json()
    } catch (error) {
      logger.error('Add-email code request failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Stage 2 of the add-email flow. Submit the code + chosen password.
   * On success the backend returns an auth token; the customer is
   * signed in with their freshly-claimed account.
   */
  async verifyAddEmail(payload: VerifyAddEmailPayload): Promise<AuthToken> {
    try {
      const response = await fetch(`${API_AUTH_URL}/add-email/verify`, {
        method: REQUEST_METHODS.POST,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }
      const data = await response.json()
      const token = (data?.data?.token ?? data?.token) as string | undefined
      const user = (data?.data?.user ?? data?.user) as Record<string, unknown> | undefined
      if (!token || !user) {
        throw new Error('Malformed verify-add-email response')
      }
      // Persist to storage so the next request (dashboard, subs, etc.)
      // picks the token up via apiClient.getAuthHeaders. Mirrors what
      // claimAccount and login do — setSession in AuthContext only
      // updates React state, not storage.
      storage.setLocal(AUTH_TOKEN_KEY, token)
      storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      logger.info('Add-email verified, account claimed')
      return { token, user: user as unknown as User }
    } catch (error) {
      logger.error('Add-email verify failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Reset password with token
   */
  async resetPassword(
    token: string,
    email: string,
    newPassword: string
  ): Promise<Response> {
    try {
      const response = await fetch(`${API_AUTH_URL}/reset-password`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          ...loginJourneyHeaders(),
        },
        body: JSON.stringify({
          token,
          email,
          password: newPassword,
          password_confirmation: newPassword,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Password reset successful')
      return response
    } catch (error) {
      logger.error('Password reset failed:', error)
      throw error
    }
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<Record<string, unknown>> {
    try {
      logger.info('Token refresh attempt')
      const token = this.getToken()
      const response = await fetch(`${API_AUTH_URL}/me`, {
        method: REQUEST_METHODS.GET,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Token refreshed successfully')
      return await response.json()
    } catch (error) {
      logger.error('Token refresh failed:', error)
      this.logout()
      throw handleApiError(error)
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      logger.info('Logout attempt')
      const token = this.getToken()
      await fetch(`${API_AUTH_URL}/logout`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (error) {
      logger.warn('Logout failed:', error)
    } finally {
      // Clear from BOTH stores — login may have used either depending
      // on the rememberMe setting, and logout shouldn't leave any
      // dangling token behind.
      storage.removeLocal(AUTH_TOKEN_KEY)
      storage.removeLocal(AUTH_USER_KEY)
      storage.removeSession(AUTH_TOKEN_KEY)
      storage.removeSession(AUTH_USER_KEY)
      logger.info('User logged out')
    }
  },

  /**
   * Get stored auth token. Reads localStorage first (the persistent
   * remember-me path); falls back to sessionStorage (the
   * non-remember-me path). The dual-store + dual-read design lets the
   * apiClient stay ignorant of which path was chosen at login time.
   */
  getToken(): string | null {
    return (
      (storage.getLocal(AUTH_TOKEN_KEY) as string | null) ??
      (storage.getSession(AUTH_TOKEN_KEY) as string | null)
    )
  },

  /**
   * Get stored user data. Same dual-store fallback as getToken().
   */
  getUser(): User | null {
    const user =
      (storage.getLocal(AUTH_USER_KEY) as string | null) ??
      (storage.getSession(AUTH_USER_KEY) as string | null)
    return user ? JSON.parse(user) : null
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken()
  },

  /**
   * Update user profile
   */
  async updateProfile(userData: Partial<User>): Promise<Record<string, unknown>> {
    try {
      logger.info('Updating profile')
      const token = this.getToken()
      const response = await fetch(`${API_AUTH_URL}/profile`, {
        method: REQUEST_METHODS.PUT,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const user = data?.data?.user
      if (user) {
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      }

      logger.info('Profile updated successfully')
      return data
    } catch (error) {
      logger.error('Profile update failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Verify email address
   */
  async verifyEmail(email: string, code: string): Promise<Record<string, unknown>> {
    try {
      const response = await fetch(`${API_AUTH_URL}/verify-email`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Email verified successfully')
      return await response.json()
    } catch (error) {
      logger.error('Email verification failed:', error)
      throw error
    }
  },

  /**
   * Change password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<Record<string, unknown>> {
    try {
      const token = this.getToken()
      const response = await fetch(`${API_AUTH_URL}/change-password`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Password changed successfully')
      return await response.json()
    } catch (error) {
      logger.error('Password change failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email: string): Promise<Record<string, unknown>> {
    try {
      const response = await fetch(`${API_AUTH_URL}/resend-verification`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      logger.info('Verification email resent')
      return await response.json()
    } catch (error) {
      logger.error('Resend verification failed:', error)
      throw error
    }
  },

  /**
   * Verify invite link is valid
   */
  async verifyInvite(email: string, expires: string, signature: string): Promise<InviteVerifyResult> {
    try {
      const response = await fetch(`${API_AUTH_URL}/invite/verify`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, expires, signature }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      logger.info('Invite verified successfully')
      return data?.data as InviteVerifyResult
    } catch (error) {
      logger.error('Invite verification failed:', error)
      throw handleApiError(error)
    }
  },

  /**
   * Accept invite and set password
   */
  async acceptInvite(email: string, expires: string, signature: string, password: string): Promise<AuthToken> {
    assertAuthAvailable()
    try {
      const response = await fetch(`${API_AUTH_URL}/invite/accept`, {
        method: REQUEST_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          expires,
          signature,
          password,
          password_confirmation: password,
        }),
      })

      if (!response.ok) {
        throw { status: response.status, body: await response.json() }
      }

      const data = await response.json()
      const token = data?.data?.token
      const user = data?.data?.user

      if (token) {
        storage.setLocal(AUTH_TOKEN_KEY, token)
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(user))
      }

      logger.info('Invite accepted successfully')
      return { token, user }
    } catch (error) {
      logger.error('Invite accept failed:', error)
      throw handleApiError(error)
    }
  },

  clearLocalAuthSession(): void {
    storage.removeLocal(AUTH_TOKEN_KEY)
    storage.removeLocal(AUTH_USER_KEY)
  },
}

export default authService
